import React, { useState, FormEvent, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../utils/api';
import './Login.css';

type TabType = 'login' | 'registration';
type RegistrationStep = 'form' | 'verify';
type PasswordResetStep = 'closed' | 'email' | 'code' | 'success';

const DOCS = {
  consent: `${process.env.PUBLIC_URL || ''}/docs/personal-data-consent.pdf`,
  offer: `${process.env.PUBLIC_URL || ''}/docs/offer.pdf`,
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, requestRegistrationCode, verifyRegistrationCode, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('login');
  const [registrationStep, setRegistrationStep] = useState<RegistrationStep>('form');
  const [verificationId, setVerificationId] = useState<number | null>(null);
  const [verificationExpiresAt, setVerificationExpiresAt] = useState<string | null>(null);
  const [code, setCode] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
  const [rememberMe, setRememberMe] = useState<boolean>(true);
  const [acceptPersonalData, setAcceptPersonalData] = useState<boolean>(false);
  const [acceptOffer, setAcceptOffer] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [passwordResetStep, setPasswordResetStep] = useState<PasswordResetStep>('closed');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('');

  // Редирект если уже авторизован
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (passwordResetStep === 'email') {
        await authAPI.requestPasswordReset(email);
        setPasswordResetStep('code');
        return;
      }
      if (passwordResetStep === 'code') {
        if (newPassword !== newPasswordConfirmation) {
          setError('Пароли не совпадают');
          return;
        }
        await authAPI.resetPassword(email, resetCode, newPassword, newPasswordConfirmation);
        setPasswordResetStep('success');
        return;
      }
      if (activeTab === 'login') {
        await login(email, password);
        navigate('/dashboard');
      } else {
        if (registrationStep === 'form') {
          if (password !== passwordConfirmation) {
            setError('Пароли не совпадают');
            setLoading(false);
            return;
          }
          if (!acceptPersonalData || !acceptOffer) {
            setError('Чтобы продолжить, примите согласие на обработку персональных данных и условия оферты');
            setLoading(false);
            return;
          }
          const res = await requestRegistrationCode(name, email, password, passwordConfirmation);
          setVerificationId(res.verification_id);
          setVerificationExpiresAt(res.expires_at);
          setRegistrationStep('verify');
        } else {
          if (!verificationId) {
            setError('Не найден запрос на подтверждение. Начните регистрацию заново.');
            setRegistrationStep('form');
            setLoading(false);
            return;
          }
          await verifyRegistrationCode(verificationId, code);
          navigate('/dashboard');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Произошла ошибка. Попробуйте еще раз.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value);
  };

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setName(e.target.value);
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setPassword(e.target.value);
  };

  const handlePasswordConfirmationChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setPasswordConfirmation(e.target.value);
  };

  const handleRememberMeChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setRememberMe(e.target.checked);
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setError('');
    setEmail('');
    setName('');
    setPassword('');
    setPasswordConfirmation('');
    setCode('');
    setVerificationId(null);
    setVerificationExpiresAt(null);
    setRegistrationStep('form');
    setAcceptPersonalData(false);
    setAcceptOffer(false);
    setPasswordResetStep('closed');
  };

  return (
    <div className="login-container">
      <div className="login-modal">
        <div className="login-header">
          <img
            src={`${process.env.PUBLIC_URL}/branding/full-logo-light.svg`}
            alt="Flypix"
            className="login-logo login-logo--light"
          />
          <img
            src={`${process.env.PUBLIC_URL}/branding/full-logo.svg`}
            alt="Flypix"
            className="login-logo login-logo--dark"
          />
        </div>

        <h1 className="login-title">
          {passwordResetStep === 'closed'
            ? activeTab === 'login' ? 'Вход в систему' : 'Создание аккаунта'
            : passwordResetStep === 'success' ? 'Пароль изменён' : 'Восстановление пароля'}
        </h1>
        <p className="login-subtitle">
          {passwordResetStep === 'closed'
            ? 'Личный кабинет видеоаналитики: камеры, детекции и отчёты в одном месте'
            : passwordResetStep === 'email'
              ? 'Укажите email, на который зарегистрирован аккаунт'
              : passwordResetStep === 'code'
                ? 'Введите код из письма и придумайте новый пароль'
                : 'Теперь можно войти с новым паролем'}
        </p>

        {passwordResetStep === 'closed' && <div className="login-tabs">
          <button 
            type="button"
            className={`login-tab ${activeTab === 'login' ? 'active' : ''}`}
            onClick={() => handleTabChange('login')}
          >
            Вход
          </button>
          <button 
            type="button"
            className={`login-tab ${activeTab === 'registration' ? 'active' : ''}`}
            onClick={() => handleTabChange('registration')}
          >
            Регистрация
          </button>
        </div>}

        {error && (
          <div className="login-error" role="alert">
            {error}
          </div>
        )}

        <form className="login-form" onSubmit={handleSubmit}>
          {passwordResetStep === 'closed' && activeTab === 'registration' && (
            <>
              {registrationStep === 'form' ? (
                <>
                  <input
                    type="text"
                    className="login-input"
                    placeholder="Имя"
                    value={name}
                    onChange={handleNameChange}
                    required
                  />
                  <input
                    type="email"
                    className="login-input"
                    placeholder="Email"
                    value={email}
                    onChange={handleEmailChange}
                    required
                  />
                  <input
                    type="password"
                    className="login-input"
                    placeholder="Пароль"
                    value={password}
                    onChange={handlePasswordChange}
                    required
                    minLength={8}
                  />
                  <input
                    type="password"
                    className="login-input"
                    placeholder="Подтвердите пароль"
                    value={passwordConfirmation}
                    onChange={handlePasswordConfirmationChange}
                    required
                    minLength={8}
                  />
                  <div className="login-legal">
                    <label className="login-checkbox-label login-checkbox-label--legal">
                      <input
                        type="checkbox"
                        className="login-checkbox"
                        checked={acceptPersonalData}
                        onChange={(e) => setAcceptPersonalData(e.target.checked)}
                        required
                      />
                      <span>
                        Я даю согласие на{' '}
                        <a
                          href={DOCS.consent}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          обработку персональных данных
                        </a>
                      </span>
                    </label>
                    <label className="login-checkbox-label login-checkbox-label--legal">
                      <input
                        type="checkbox"
                        className="login-checkbox"
                        checked={acceptOffer}
                        onChange={(e) => setAcceptOffer(e.target.checked)}
                        required
                      />
                      <span>
                        Я принимаю условия{' '}
                        <a
                          href={DOCS.offer}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Оферты
                        </a>
                      </span>
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <div className="login-hint">
                    Мы отправили код на <b>{email}</b>
                    {verificationExpiresAt ? (
                      <>
                        <br />
                        Действует до: {new Date(verificationExpiresAt).toLocaleString()}
                      </>
                    ) : null}
                  </div>
                  <input
                    type="text"
                    className="login-input"
                    placeholder="Код из письма"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\s+/g, ''))}
                    required
                    inputMode="numeric"
                    maxLength={10}
                  />
                  <button
                    type="button"
                    className="login-button login-button--secondary"
                    disabled={loading}
                    onClick={() => {
                      setError('');
                      setCode('');
                      setVerificationId(null);
                      setVerificationExpiresAt(null);
                      setRegistrationStep('form');
                    }}
                  >
                    Назад
                  </button>
                </>
              )}
            </>
          )}
          {passwordResetStep === 'closed' && activeTab === 'login' && (
            <>
              <input
                type="email"
                className="login-input"
                placeholder="Email"
                value={email}
                onChange={handleEmailChange}
                required
              />
              <input
                type="password"
                className="login-input"
                placeholder="Пароль"
                value={password}
                onChange={handlePasswordChange}
                required
                minLength={8}
              />
            </>
          )}

          {passwordResetStep === 'email' && (
            <input type="email" className="login-input" placeholder="Email" value={email} onChange={handleEmailChange} required autoFocus />
          )}

          {passwordResetStep === 'code' && (
            <>
              <div className="login-hint">Код отправлен на <b>{email}</b>. Он действует 15 минут.</div>
              <input type="text" className="login-input" placeholder="Код из письма" value={resetCode} onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required inputMode="numeric" autoFocus />
              <input type="password" className="login-input" placeholder="Новый пароль" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} />
              <input type="password" className="login-input" placeholder="Подтвердите новый пароль" value={newPasswordConfirmation} onChange={(e) => setNewPasswordConfirmation(e.target.value)} required minLength={8} />
            </>
          )}

          {passwordResetStep === 'success' && (
            <div className="login-hint">Пароль успешно изменён. Все старые сессии завершены.</div>
          )}
          
          {passwordResetStep !== 'success' && <button
            type="submit"
            className="login-button"
            disabled={
              loading ||
              (activeTab === 'registration' &&
                registrationStep === 'form' &&
                (!acceptPersonalData || !acceptOffer))
            }
          >
            {loading
              ? 'Загрузка...'
              : passwordResetStep === 'email'
                ? 'Отправить код'
                : passwordResetStep === 'code'
                  ? 'Сохранить новый пароль'
              : activeTab === 'login'
                ? 'Войти'
                : registrationStep === 'form'
                  ? 'Отправить код'
                  : 'Подтвердить и войти'}
          </button>}

          {passwordResetStep !== 'closed' && (
            <button
              type="button"
              className={`login-button${passwordResetStep === 'success' ? '' : ' login-button--secondary'}`}
              disabled={loading}
              onClick={() => {
                setError('');
                setResetCode('');
                setNewPassword('');
                setNewPasswordConfirmation('');
                setPasswordResetStep('closed');
              }}
            >
              {passwordResetStep === 'success' ? 'Перейти ко входу' : 'Назад ко входу'}
            </button>
          )}

          {passwordResetStep === 'closed' && activeTab === 'login' && <div className="login-options">
            <label className="login-checkbox-label">
              <input
                type="checkbox"
                className="login-checkbox"
                checked={rememberMe}
                onChange={handleRememberMeChange}
              />
              <span>Запомнить меня</span>
            </label>
            <button
              type="button"
              className="login-forgot"
              onClick={() => {
                setError('');
                setPassword('');
                setPasswordResetStep('email');
              }}
            >
              Забыли пароль?
            </button>
          </div>}

          {/* <div className="login-divider">
            <span className="login-divider-line"></span>
            <span className="login-divider-text">или</span>
            <span className="login-divider-line"></span>
          </div>

          <div className="login-social">
            <button type="button" className="login-social-button">
              <img src="/logo/google.svg" alt="Google" className="login-social-icon" />
              <span>Google</span>
            </button>
            <button type="button" className="login-social-button">
              <img src="/logo/apple.svg" alt="Apple" className="login-social-icon" />
              <span>Apple</span>
            </button>
          </div> */}
        </form>
      </div>
    </div>
  );
};

export default Login;
